import { Injectable, inject } from '@angular/core';
import { Observable, of, forkJoin, from } from 'rxjs';
import { map, mergeMap, toArray, catchError } from 'rxjs/operators';
import { PartyService } from '../entity-services/party.service';
import { PeopleService } from '../entity-services/people.service';
import { BaaService } from '../entity-services/baa.service';
import { CoiService } from '../entity-services/coi.service';
import { LegalEntityService } from '../entity-services/legal-entity.service';
import { SiteService } from '../entity-services/site.service';
import { Contract } from '../../objects/contract';

/**
 * RehydrationService - Centralized service for hydrating entity objects
 *
 * This service replaces ID references with full entity objects by fetching them
 * from their respective services. It works in parallel for optimal performance.
 *
 * Example: Converting contract.directVendor from "VENDOR123" to full vendor object
 */
@Injectable({
  providedIn: 'root'
})
export class RehydrationService {
  private partyService = inject(PartyService);
  private peopleService = inject(PeopleService);
  private baaService = inject(BaaService);
  private coiService = inject(CoiService);
  private legalEntityService = inject(LegalEntityService);
  private siteService = inject(SiteService);

  /**
   * Hydrates a contract by replacing all ID references with full objects
   *
   * @param contract The contract with ID references
   * @returns Observable of the fully hydrated contract
   */
  public hydrateContract(contract: Contract): Observable<Contract> {
    if (!contract) {
      return of(contract);
    }

    const hydrationTasks: Observable<any>[] = [];
    const hydratedContract = { ...contract };

    // Party/Vendor fields - single IDs
    const vendorFields = [
      { key: 'directVendor', id: contract.directVendor },
      { key: 'parentParty', id: contract.parentParty },
      { key: 'party', id: contract.party }
    ];

    vendorFields.forEach(field => {
      if (field.id && typeof field.id === 'string') {
        hydrationTasks.push(
          this.partyService.getPartyObject(field.id).pipe(
            map(result => ({ key: field.key, result })),
            catchError(() => of({ key: field.key, result: null }))
          )
        );
      }
    });

    // NOTE on the `?? id` fallbacks in the array hydrations below.
    //
    // Each getXObject() resolves an id against a progressively-paginated cache and
    // returns null when that cache hasn't filled yet (they take(1) off a
    // BehaviorSubject seeded with []). Results are collected with toArray(), so a
    // null lands in the array and downstream consumers read a property off it —
    // PermissionsUtilityService.filterContracts did exactly that, and a throw there
    // escapes into ContractService's combineLatest subscriber, which stops contract
    // visibility updating at all rather than degrading.
    //
    // Falling back to the original id string beats dropping the entry: these fields
    // are STORED as bare id strings anyway, consumers already accept either shape,
    // and the permission filter can still match on the raw id. Dropping would
    // silently hide a contract the user is entitled to see.
    // Party/Vendor arrays
    if (contract.otherParties && Array.isArray(contract.otherParties) && contract.otherParties.length > 0) {
      const vendorIds = contract.otherParties.filter(p => typeof p === 'string');
      if (vendorIds.length > 0) {
        hydrationTasks.push(
          from(vendorIds).pipe(
            mergeMap(id => this.partyService.getPartyObject(id as string).pipe(map(r => r ?? id))),
            toArray(),
            map(results => ({ key: 'otherParties', result: results })),
            catchError(() => of({ key: 'otherParties', result: [] }))
          )
        );
      }
    }

    // Subcontractors array (similar to otherParties)
    if (contract.subcontractorVendorNums && Array.isArray(contract.subcontractorVendorNums) && contract.subcontractorVendorNums.length > 0) {
      const subcontractorIds = contract.subcontractorVendorNums.filter(id => typeof id === 'string');
      if (subcontractorIds.length > 0) {
        hydrationTasks.push(
          from(subcontractorIds).pipe(
            mergeMap(id => this.partyService.getPartyObject(id as string).pipe(map(r => r ?? id))),
            toArray(),
            map(results => ({ key: 'subcontractors', result: results })),
            catchError(() => of({ key: 'subcontractors', result: [] }))
          )
        );
      }
    }

    // User/People fields - single IDs
    const userFields = [
      { key: 'primaryResponsibleParty', id: contract.primaryResponsibleParty },
      { key: 'internalSignatory', id: contract.internalSignatory },
      { key: 'yourContractCoordinator', id: contract.yourContractCoordinator },
      { key: 'partiesSignatory', id: contract.partiesSignatory },
      { key: 'partyContractCoordinator', id: contract.partyContractCoordinator }
    ];

    userFields.forEach(field => {
      if (field.id && typeof field.id === 'string') {
        hydrationTasks.push(
          this.peopleService.getPersonObject(field.id).pipe(
            map(result => ({ key: field.key, result })),
            catchError(() => of({ key: field.key, result: null }))
          )
        );
      }
    });

    // Legal Entities array
    if (Array.isArray(contract.legalEntities) && contract.legalEntities.length > 0) {
      const entityIds = contract.legalEntities.filter(e => typeof e === 'string');
      if (entityIds.length > 0) {
        hydrationTasks.push(
          from(entityIds).pipe(
            mergeMap(id => this.legalEntityService.getLegalEntityObject(id as string).pipe(map(r => r ?? id))),
            toArray(),
            map(results => ({ key: 'legalEntities', result: results })),
            catchError(() => of({ key: 'legalEntities', result: [] }))
          )
        );
      }
    }

    // Sites array
    if (Array.isArray(contract.sites) && contract.sites.length > 0) {
      const siteIds = contract.sites.filter(s => typeof s === 'string');
      if (siteIds.length > 0) {
        hydrationTasks.push(
          from(siteIds).pipe(
            mergeMap(id => this.siteService.getSiteObject(id as string).pipe(map(r => r ?? id))),
            toArray(),
            map(results => ({ key: 'sites', result: results })),
            catchError(() => of({ key: 'sites', result: [] }))
          )
        );
      }
    }

    // NOTE: masterContract is NOT hydrated to avoid circular dependency
    // (ContractService uses RehydrationService, so we can't inject ContractService here)
    // The masterContract field will remain as a contractId string

    // Direct BAA - single ID
    if (contract.directBaa && typeof contract.directBaa === 'string') {
      hydrationTasks.push(
        this.baaService.getBaaObject(contract.directBaa).pipe(
          map(result => ({ key: 'directBaa', result })),
          catchError(() => of({ key: 'directBaa', result: null }))
        )
      );
    }

    // Direct COI - single ID
    if (contract.directCoi && typeof contract.directCoi === 'string') {
      hydrationTasks.push(
        this.coiService.getCoiObject(contract.directCoi).pipe(
          map(result => ({ key: 'directCoi', result })),
          catchError(() => of({ key: 'directCoi', result: null }))
        )
      );
    }

    // If no hydration needed, return the original contract
    if (hydrationTasks.length === 0) {
      return of(hydratedContract);
    }

    // Execute all hydration tasks in parallel
    return forkJoin(hydrationTasks).pipe(
      map(results => {
        results.forEach(({ key, result }) => {
          if (result !== null) {
            hydratedContract[key] = result;
          }
        });
        return hydratedContract;
      }),
      catchError(error => {
        console.error('RehydrationService | Error hydrating contract:', error);
        return of(contract); // Return original contract on error
      })
    );
  }
}
